import React, { useState, useEffect } from 'react';
import { getSeverity } from "./ClashParser";
import ProcoreConnect from "./ProcoreConnect";
import '../styles/ClashAnalyzer.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const DISCIPLINE_KEYWORDS = {
  Mechanical:    ['duct', 'hvac', 'air handling', 'diffuser', 'grille', 'fan', 'exhaust', 'supply air', 'return air'],
  Plumbing:      ['pipe', 'plumbing', 'drain', 'water', 'sanitary', 'vent', 'sprinkler', 'domestic'],
  Electrical:    ['conduit', 'cable tray', 'electrical', 'panel', 'wire', 'junction', 'transformer'],
  Structural:    ['steel', 'beam', 'column', 'joist', 'concrete', 'slab', 'footing', 'rebar', 'astm', 'wide flange'],
  Architectural: ['wall', 'ceiling', 'floor', 'door', 'window', 'partition', 'finish', 'paint', 'ivory'],
  Civil:         ['grade', 'site', 'paving', 'utility', 'storm', 'sewer'],
};

function detectDiscipline(itemName = '') {
  const lower = itemName.toLowerCase();
  for (const [disc, keywords] of Object.entries(DISCIPLINE_KEYWORDS)) {
    if (keywords.some(k => lower.includes(k))) return disc;
  }
  return 'General';
}

function detectDisciplines(clash) {
  const d1 = detectDiscipline(clash.item1.itemName);
  const d2 = detectDiscipline(clash.item2.itemName);
  return d1 === d2 ? d1 : `${d1} / ${d2}`;
}

function getPriority(distance) {
  const d = Math.abs(distance);
  if (d >= 0.5) return 'Critical';
  if (d >= 0.2) return 'High';
  if (d >= 0.05) return 'Medium';
  return 'Low';
}

function formatClashPoint(cp) {
  if (!cp || cp.x === null) return 'Not specified';
  return `X: ${cp.x.toFixed(3)}m, Y: ${cp.y.toFixed(3)}m, Z: ${cp.z.toFixed(3)}m`;
}

function buildCopyText(fields, aiDraft) {
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  return `RFI DRAFT — ${fields.title}
${'─'.repeat(60)}
Discipline:       ${fields.discipline}
Priority:         ${fields.priority}
Location:         ${fields.location}
Assigned To:      ${fields.assignedTo || 'TBD'}
Date:             ${today}

DESCRIPTION
${aiDraft.description || '[No description generated — add manually]'}

SUGGESTED ACTION
${aiDraft.suggestedAction || '[No action generated — add manually]'}

CLASH REFERENCE
  Clash Name:     ${fields.clashName}
  Element 1:      ${fields.element1} (ID: ${fields.elementId1}) — ${fields.layer1}
  Element 2:      ${fields.element2} (ID: ${fields.elementId2}) — ${fields.layer2}
  Penetration:    ${fields.penetration}
  Clash Point:    ${fields.clashPoint}
  Clash Type:     Hard Clash
  Report Date:    ${fields.dateCreated}

${'─'.repeat(60)}
Generated by POMAR Clash · TechDen Solutions · pomar.ai`;
}

export default function RFIModal({ clash, onClose }) {
  const sev = getSeverity(clash.distance);

  const [fields, setFields] = useState({
    title:      `${clash.item1.itemName} vs ${clash.item2.itemName} — ${clash.item1.layer}`,
    discipline: detectDisciplines(clash),
    priority:   getPriority(clash.distance),
    location:   `${clash.item1.layer} — ${formatClashPoint(clash.clashPoint)}`,
    assignedTo: '',
    clashName:  clash.name,
    element1:   clash.item1.itemName,
    element2:   clash.item2.itemName,
    elementId1: clash.item1.elementId,
    elementId2: clash.item2.elementId,
    layer1:     clash.item1.layer,
    layer2:     clash.item2.layer,
    penetration: clash.distanceRaw,
    clashPoint: formatClashPoint(clash.clashPoint),
    dateCreated: clash.dateCreated,
  });

  const [aiDraft, setAiDraft]     = useState({ description: '', suggestedAction: '' });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError]     = useState('');
  const [copied, setCopied]       = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { generateRFI(); }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const generateRFI = async () => {
    setAiLoading(true);
    setAiError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/clash/draft-rfi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clashName:  clash.name,
          status:     clash.status,
          distance:   clash.distanceRaw,
          item1:      clash.item1,
          item2:      clash.item2,
          clashPoint: formatClashPoint(clash.clashPoint),
          discipline: detectDisciplines(clash),
          priority:   getPriority(clash.distance),
        }),
      });
      const data = await res.json();
      setAiDraft({
        description:     data.description     || '',
        suggestedAction: data.suggestedAction || '',
      });
    } catch {
      setAiError('Could not reach AI service. You can edit the fields manually and still copy the RFI.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCopy = () => {
    const text = buildCopyText(fields, aiDraft);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  // TODO(unsaved-changes guard): this modal's fields/aiDraft are exactly the
  // kind of "in-progress Clash annotation" the guard is meant to protect —
  // import useUnsavedChanges from '../../../contexts/UnsavedChangesContext'
  // and call setDirty(true, 'an RFI draft') from setField/setAi below (skip
  // the initial generateRFI() population on mount, same as the worked
  // example's isMountedWithInitialValue pattern). There's no persistence API
  // for RFI drafts today — handleCopy is the closest thing to a "save", so
  // registerSaveHandler(() => handleCopy()) and setDirty(false) on copy/close
  // is the reasonable wiring once this needs enforcing.
  const setField = (key, val) => setFields(f => ({ ...f, [key]: val }));
  const setAi    = (key, val) => setAiDraft(f => ({ ...f, [key]: val }));

  return (
    <div className="rfi-overlay" onClick={onClose}>
      <div className="rfi-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Draft RFI">

        <div className="rfi-modal-header">
          <div>
            <div className="rfi-modal-badge">POMAR Clash · RFI Draft</div>
            <h2 className="rfi-modal-title">Draft RFI</h2>
          </div>
          <button className="rfi-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="rfi-clash-strip">
          <span className="rfi-clash-name">{clash.name}</span>
          <span className="rfi-sev-pill" style={{ background: sev.color + '20', color: sev.color }}>{sev.label}</span>
          <span className="rfi-strip-detail">{clash.distanceRaw} penetration</span>
          <span className="rfi-strip-detail">{clash.item1.layer}</span>
          <span className="rfi-strip-detail">{clash.status}</span>
        </div>

        <div className="rfi-modal-body">

          <section className="rfi-section">
            <p className="rfi-section-label">RFI Fields</p>
            <div className="rfi-field-grid">

              <div className="rfi-field rfi-field-full">
                <label className="rfi-label">Title</label>
                <input className="rfi-input" value={fields.title} onChange={e => setField('title', e.target.value)} />
              </div>

              <div className="rfi-field">
                <label className="rfi-label">Discipline</label>
                <select className="rfi-select" value={fields.discipline} onChange={e => setField('discipline', e.target.value)}>
                  {['Mechanical','Plumbing','Electrical','Structural','Architectural','Civil',
                    'Mechanical / Structural','Mechanical / Plumbing','Plumbing / Structural',
                    'Electrical / Structural','General'].map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div className="rfi-field">
                <label className="rfi-label">Priority</label>
                <select className="rfi-select" value={fields.priority} onChange={e => setField('priority', e.target.value)}>
                  {['Critical','High','Medium','Low'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div className="rfi-field rfi-field-full">
                <label className="rfi-label">Location</label>
                <input className="rfi-input" value={fields.location} onChange={e => setField('location', e.target.value)} />
              </div>

              <div className="rfi-field rfi-field-full">
                <label className="rfi-label">Assigned To</label>
                <input
                  className="rfi-input"
                  placeholder="e.g. MEP Subcontractor, Structural Engineer, Architect…"
                  value={fields.assignedTo}
                  onChange={e => setField('assignedTo', e.target.value)}
                />
              </div>

            </div>
          </section>

          <section className="rfi-section">
            <div className="rfi-section-header">
              <p className="rfi-section-label">AI-Generated Narrative</p>
              <button className="rfi-regenerate-btn" onClick={generateRFI} disabled={aiLoading}>
                {aiLoading ? 'Generating…' : '↺ Regenerate'}
              </button>
            </div>

            {aiError && <p className="rfi-ai-error">{aiError}</p>}

            {aiLoading ? (
              <div className="rfi-ai-loading">
                <div className="clash-spinner" />
                <span>Claude is drafting your RFI narrative…</span>
              </div>
            ) : (
              <>
                <div className="rfi-field">
                  <label className="rfi-label">Description</label>
                  <textarea
                    className="rfi-textarea"
                    rows={5}
                    value={aiDraft.description}
                    onChange={e => setAi('description', e.target.value)}
                    placeholder="AI description will appear here. You can edit before copying."
                  />
                </div>
                <div className="rfi-field" style={{ marginTop: 12 }}>
                  <label className="rfi-label">Suggested Action</label>
                  <textarea
                    className="rfi-textarea"
                    rows={3}
                    value={aiDraft.suggestedAction}
                    onChange={e => setAi('suggestedAction', e.target.value)}
                    placeholder="AI suggested action will appear here."
                  />
                </div>
              </>
            )}
          </section>

          <section className="rfi-section">
            <p className="rfi-section-label">Clash Reference</p>
            <div className="rfi-ref-grid">
              <div><span className="rfi-ref-key">Element 1</span><span className="rfi-ref-val">{clash.item1.itemName}</span></div>
              <div><span className="rfi-ref-key">Element ID 1</span><span className="rfi-ref-val">{clash.item1.elementId}</span></div>
              <div><span className="rfi-ref-key">Element 2</span><span className="rfi-ref-val">{clash.item2.itemName}</span></div>
              <div><span className="rfi-ref-key">Element ID 2</span><span className="rfi-ref-val">{clash.item2.elementId}</span></div>
              <div><span className="rfi-ref-key">Layer 1</span><span className="rfi-ref-val">{clash.item1.layer}</span></div>
              <div><span className="rfi-ref-key">Layer 2</span><span className="rfi-ref-val">{clash.item2.layer}</span></div>
              <div><span className="rfi-ref-key">Penetration</span><span className="rfi-ref-val" style={{ color: sev.color, fontWeight: 600 }}>{clash.distanceRaw}</span></div>
              <div><span className="rfi-ref-key">Clash Point</span><span className="rfi-ref-val">{formatClashPoint(clash.clashPoint)}</span></div>
            </div>
          </section>

        </div>

        <div className="rfi-modal-footer">
          <ProcoreConnect
            userId={window.localStorage.getItem("constructmail_userId")}
            rfiData={{
              title: fields.title,
              description: aiDraft.description,
              priority: fields.priority,
              assigneeId: null,
              clashName: fields.clashName,
            }}
          />
          <p className="rfi-footer-hint">All fields are editable before copying</p>
          <div className="rfi-footer-actions">
            <button className="clash-btn-secondary" onClick={onClose}>Cancel</button>
            <button className="clash-btn-primary" onClick={handleCopy} disabled={aiLoading}>
              {copied ? '✓ Copied to clipboard!' : '📋 Copy for Procore / Kahua'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
